import { ComponentProps, useRef, useState } from "react";
import { SingleSelect } from "@bonliva-crm/ui";
import { useFormField } from "@clientUtils";
import { useUpdateEffect } from "@hooks";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";
import { SingleValueChip } from "./_select/single-value-chip";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import locale from "@bonliva-crm/locale";

export type FitnessFilterData = "fit" | "unfit" | "notContactable";

type FitnessFilterProps = FilterWithFavouriteProps;
type Option = { label?: string; value: FitnessFilterData };

export const fitnessFilterOptions: Option[] = [
  { label: locale.FitConsultants, value: "fit" },
  { label: locale.UnfitConsultants, value: "unfit" },
  { label: locale.NotContactableConsultants, value: "notContactable" }
];

export const FitnessFilter = (props: FitnessFilterProps) => {
  const selectRef = useRef<HTMLElement>(null);
  const {
    field,
    fieldState,
    setFieldValue,
    formContext: { resetField }
  } = useFormField<FitnessFilterData>(props.name);
  const [expanded, setExpanded] = useState(fieldState.isDirty);
  const title = props.label ?? locale.Fitness;
  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current?.focus();
    }
  }, [expanded]);

  const clear = () => resetField(props.name);

  const value = {
    label: fitnessFilterOptions.find((option) => option.value === field?.value)?.label,
    value: field?.value
  };

  const commonProps: ComponentProps<typeof SingleSelect<Option>> = {
    options: fitnessFilterOptions,
    value,
    onChange: (selected) => {
      setFieldValue(selected?.value as FitnessFilterData, { shouldDirty: true });
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: title,
    isSearchable: false,
    maxMenuHeight: 600,
    showBadge: !!field?.value,
    optionType: "radio",
    isClearable: !!field?.value,
    onClear: () => resetField(props.name)
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={clear}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <SingleSelect<Option>
          {...commonProps}
          selectRef={selectRef}
          controlShouldRenderValue
          openMenuOnFocus
          menuPortalTarget={document.body}
          showBadge={false}
          isClearable={false}
          placeholder={locale.SelectValue}
          menuPlacement="auto"
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
          components={{
            SingleValue: (p) => (
              <SingleValueChip {...p} onDelete={() => resetField(props.name)} />
            )
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <SingleSelect<Option>
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
};
